import type {
  AIFlag,
  AIFlagCode,
  AIFlagSeverity,
  ExtractPrescriptionOutput,
} from '@/types/db';

/**
 * Default severity + human message for each AI flag code.
 * ARCHITECTURE §4.b — none of Claude's auto-emitted codes default to `block`;
 * a block-level flag is only produced when a caregiver explicitly resolves it
 * by clearing a required field during Review.
 */
const FLAG_META: Record<
  AIFlagCode,
  { severity: AIFlagSeverity; message: string }
> = {
  unreadable_drug_name: {
    severity: 'warn',
    message: "Drug name couldn't be read — type it from the paper",
  },
  unreadable_dosage: {
    severity: 'warn',
    message: "Dosage couldn't be read — type it from the paper",
  },
  unreadable_date: {
    severity: 'warn',
    message: "Date couldn't be read — type it from the paper",
  },
  name_transliterated_from_arabic: {
    severity: 'info',
    message: 'Drug name transliterated from Arabic — verify spelling',
  },
  date_converted_from_hijri: {
    severity: 'warn',
    message: 'Date converted from Hijri — verify the day',
  },
  duplicate_candidate: {
    severity: 'warn',
    message: 'Possible duplicate of an existing prescription',
  },
  continuation_candidate: {
    severity: 'info',
    message: 'Looks like a refill of an existing course',
  },
  duration_atypical: {
    severity: 'warn',
    message: 'Duration outside typical range — verify against the paper',
  },
  low_overall_confidence: {
    severity: 'warn',
    message: 'Low overall confidence — double-check every field',
  },
  date_assumed_dmy: {
    severity: 'info',
    message: 'Date read as DD/MM/YYYY — verify the day and month',
  },
  arabic_indic_digits_normalized: {
    severity: 'info',
    message: 'Arabic-Indic digits converted to Western digits',
  },
  chief_complaint_not_diagnosis: {
    severity: 'info',
    message: 'Paper shows a symptom, not a diagnosis — edit if needed',
  },
  alternative_drug: {
    severity: 'warn',
    message: 'Prescriber wrote an "or" alternative — pick one before dispensing',
  },
  alternating_regimen: {
    severity: 'info',
    message: 'Alternates with another medication on a rotating schedule — see notes',
  },
  compound_preparation: {
    severity: 'warn',
    message: 'Multi-ingredient recipe — all parts are in the dose field',
  },
  handwritten_low_legibility: {
    severity: 'warn',
    message: 'Handwriting was marginal — verify against the paper',
  },
  partial_patient_info: {
    severity: 'info',
    message: 'Patient name or date field was blank on the paper',
  },
  // 2-call pipeline (Day-3) — emitted by classifier + specialty extractors
  field_low_confidence: {
    severity: 'warn',
    message: 'A field was hard to read — verify against the paper',
  },
  field_partial: {
    severity: 'info',
    message: 'A field was truncated — full text was too long to capture',
  },
  dosage_atypical: {
    severity: 'warn',
    message: 'Dosage outside typical range — verify against the paper',
  },
  interaction_check_needed: {
    severity: 'info',
    message: 'Worth a pharmacist check against current meds',
  },
  value_out_of_range: {
    severity: 'warn',
    message: 'Lab value outside typical range — verify the result',
  },
  vital_unit_unclear: {
    severity: 'warn',
    message: 'Vital sign unit was unclear — verify against the paper',
  },
  vital_out_of_range: {
    severity: 'warn',
    message: 'Vital sign outside typical range — verify the reading',
  },
  report_conclusion_missing: {
    severity: 'info',
    message: 'No impression / conclusion section found on this report',
  },
  critical_finding_in_conclusion: {
    severity: 'warn',
    message: 'The radiologist used urgent language — review the conclusion',
  },
  vaccine_overdue: {
    severity: 'info',
    message: 'A vaccine on the standard schedule looks overdue',
  },
  vaccine_unrecognized: {
    severity: 'warn',
    message: "Couldn't map this vaccine to a standard name — verify",
  },
  image_too_blurry: {
    severity: 'warn',
    message: 'Image was blurry — retake the photo if details are wrong',
  },
  partial_image: {
    severity: 'warn',
    message: 'Part of the document was cut off — retake the photo',
  },
  unrelated_content: {
    severity: 'warn',
    message: "This doesn't look like a medical document",
  },
  refusal: {
    severity: 'warn',
    message: 'The reader declined to extract — type the details instead',
  },
};

export function codeToFlag(code: AIFlagCode, context?: string): AIFlag {
  const meta = FLAG_META[code];
  return {
    code,
    severity: meta.severity,
    message: context ? `${meta.message} (${context})` : meta.message,
  };
}

export function flagSeverity(code: AIFlagCode): AIFlagSeverity {
  return FLAG_META[code].severity;
}

export function flagMessage(code: AIFlagCode): string {
  return FLAG_META[code].message;
}

/**
 * Flatten the extraction output's per-medication flags + top-level flags into
 * a single AIFlag[] for storage on `timeline_events.ai_flags`. Per-med flags
 * carry a `(drug: <name or index>)` suffix so they remain legible later.
 */
export function buildFlagsFromExtraction(
  extraction: ExtractPrescriptionOutput,
): AIFlag[] {
  const out: AIFlag[] = [];
  for (const code of extraction.flags ?? []) {
    out.push(codeToFlag(code));
  }
  extraction.medications?.forEach((med, idx) => {
    const context = med.drug_name?.trim() || `drug ${idx + 1}`;
    for (const code of med.flags ?? []) {
      out.push(codeToFlag(code, context));
    }
  });
  return out;
}

export function hasBlockingFlags(flags: AIFlag[]): boolean {
  return flags.some((f) => f.severity === 'block');
}
